import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../lib/api';
import logo from '../assets/chitconnect-logo-blue.png';

export default function Messages() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [conversations, setConversations] = useState([]);
  const [myGroups, setMyGroups] = useState([]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError('');

      try {
        const [dmRes, groupsRes] = await Promise.all([
          apiJson('/api/messages/dm/conversations'),
          apiJson('/api/messages/group/summary'),
        ]);

        if (!mounted) return;

        // DM inbox endpoint returns { ok: true, conversations: [...] }
        if (dmRes?.ok && dmRes.data?.ok) setConversations(dmRes.data.conversations || []);
        else if (dmRes?.ok && Array.isArray(dmRes.data)) setConversations(dmRes.data);
        else setConversations([]);

        // Group chat summary returns { ok: true, groups: [...] }
        if (groupsRes?.ok && groupsRes.data?.ok) setMyGroups(groupsRes.data.groups || []);
        else if (groupsRes?.ok && Array.isArray(groupsRes.data)) setMyGroups(groupsRes.data);
        else setMyGroups([]);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load messages.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="main-content messages-page">
      {/* Keep consistent spacing so the ☰ button never overlaps the title */}
      <img src={logo} alt="ChitConnect Logo" className="group-logo" />

      <h2 className="page-title">Messages</h2>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="messages-grid">
          {/* Direct messages */}
          <section className="messages-panel">
            <div className="messages-panel-header">
              <h3>Direct messages</h3>
              <div className="messages-hint">Start a chat from a group member list</div>
            </div>

            {conversations.length === 0 ? (
              <div className="messages-empty">No conversations yet.</div>
            ) : (
              <div className="messages-list">
                {conversations.map((c) => (
                  <Link
                    key={c.conversation_id}
                    to={`/messages/dm/${c.conversation_id}`}
                    className="messages-item"
                  >
                    <div className="messages-item-top">
                      <div className="messages-item-title">{c.other_user?.name || 'User'}</div>
                      {c.unread_count > 0 && <div className="messages-badge">{c.unread_count}</div>}
                    </div>
                    <div className="messages-preview">{c.last_message?.body || '—'}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Group chats */}
          <section className="messages-panel">
            <div className="messages-panel-header">
              <h3>Group chats</h3>
              <div className="messages-hint">Chats are per chit group</div>
            </div>

            {myGroups.length === 0 ? (
              <div className="messages-empty">You’re not in any groups yet.</div>
            ) : (
              <div className="messages-list">
                {myGroups.map((g) => (
                  <Link key={g.group_id || g.id} to={`/messages/group/${g.group_id || g.id}`} className="messages-item">
                    <div className="messages-item-top">
                      <div className="messages-item-title">{g.group_name || g.name}</div>
                      {(g.unread_count || 0) > 0 && (
                        <div className="messages-badge">{g.unread_count > 99 ? '99+' : g.unread_count}</div>
                      )}
                    </div>
                    <div className="messages-preview">{g.last_message_body || 'Group chat'}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      <div className="messages-tip">
        Tip: To start a new direct chat, open a group and go to Members, then tap “Message”.
      </div>
    </div>
  );
}
